/**
 * F-008 — Skeleton primitive.
 *
 * Renders a pulsing placeholder block that screen readers ignore
 * (the parent should set aria-busy="true"). Variants pre-set
 * reasonable dimensions; pass `width` / `height` to override.
 */
type SkeletonVariant = 'card' | 'row' | 'text' | 'title' | 'block';

interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  className?: string;
  /** For test IDs. */
  testId?: string;
}

export default function Skeleton({
  variant = 'text',
  width,
  height,
  className,
  testId = 'skeleton',
}: SkeletonProps) {
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;
  return (
    <span
      className={`skeleton skeleton--${variant}${className ? ` ${className}` : ''}`}
      aria-hidden="true"
      data-testid={testId}
      style={style}
    />
  );
}

/**
 * Convenience: a row of skeleton cells matching the data-table layout
 * used by the submissions list. Renders one `<tr>` so the host can
 * `Array.from({length:5}).map(...)`.
 */
export function SkeletonRow({ cells = 8, testId }: { cells?: number; testId?: string }) {
  return (
    <tr className="skeleton-row" data-testid={testId} aria-hidden="true">
      {Array.from({ length: cells }, (_, i) => (
        <td key={i}>
          <Skeleton variant="text" />
        </td>
      ))}
    </tr>
  );
}
